import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Users, ArrowLeft, Eye, Brain, Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, AlertCircle } from 'lucide-react';
import { collection, getDocs, query, where, doc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../../lib/firebase';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { Toast } from '../../components/Toast';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  createColumnHelper,
  flexRender,
} from '@tanstack/react-table';

interface Respondent {
  id: string;
  userId: string;
  fullName: string;
  email: string;
  assessmentStatus: 'pending' | 'completed';
  completedAt?: Date;
  totalAssessments: number;
  aiAnalysisEnabled: boolean;
}

const columnHelper = createColumnHelper<Respondent>();

export function RespondentsList() {
  const navigate = useNavigate();
  const [respondents, setRespondents] = useState<Respondent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [updatingAI, setUpdatingAI] = useState<string | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [globalFilter, setGlobalFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [aiFilter, setAiFilter] = useState('all');

  const handleAIToggle = async (respondentId: string, currentStatus: boolean) => {
    try {
      setUpdatingAI(respondentId);
      
      const respondentRef = doc(db, 'respondents', respondentId);
      await updateDoc(respondentRef, {
        aiAnalysisEnabled: !currentStatus,
        updatedAt: new Date()
      });
      
      // Update local state
      setRespondents(prev => prev.map(respondent => 
        respondent.id === respondentId 
          ? { ...respondent, aiAnalysisEnabled: !currentStatus }
          : respondent
      ));
      
      setToastMessage(`AI Analysis ${!currentStatus ? 'enabled' : 'disabled'} successfully`);
      setToastType('success');
      setShowToast(true);
      
    } catch (err) {
      console.error('Error updating AI access:', err);
      setToastMessage('Failed to update AI analysis access');
      setToastType('error');
      setShowToast(true);
    } finally {
      setUpdatingAI(null);
    }
  };

  const columns = useMemo(() => [
    columnHelper.accessor('fullName', {
      header: 'Name',
      cell: (info) => (
        <div className="font-medium text-gray-900">
          {info.getValue()}
        </div>
      ),
    }),
    columnHelper.accessor('email', {
      header: 'Email',
      cell: (info) => (
        <div className="text-gray-600">
          {info.getValue()}
        </div>
      ),
    }),
    columnHelper.accessor('assessmentStatus', {
      header: 'Status',
      cell: (info) => (
        <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${
          info.getValue() === 'completed' 
            ? 'bg-emerald-100 text-emerald-800' 
            : 'bg-yellow-100 text-yellow-800'
        }`}>
          {info.getValue() === 'completed' ? 'Completed' : 'Pending'}
        </span>
      ),
      filterFn: (row, columnId, value) => {
        if (value === 'all') return true;
        return row.getValue(columnId) === value;
      },
    }),
    columnHelper.accessor('completedAt', {
      header: 'Completed Date',
      cell: (info) => {
        const date = info.getValue();
        const totalAssessments = info.row.original.totalAssessments;
        return date ? (
          <div className="text-gray-600">
            <div>{date.toLocaleDateString()}</div>
            <div className="text-xs text-gray-500">
              {totalAssessments} assessment{totalAssessments === 1 ? '' : 's'}
            </div>
          </div>
        ) : (
          <div className="text-gray-400">-</div>
        );
      },
    }),
    columnHelper.accessor('aiAnalysisEnabled', {
      header: 'AI Analysis',
      cell: (info) => {
        const respondent = info.row.original;
        return (
          <button
            onClick={() => handleAIToggle(respondent.id, respondent.aiAnalysisEnabled)}
            disabled={updatingAI === respondent.id}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-50 flex items-center gap-2 ${
              respondent.aiAnalysisEnabled
                ? 'bg-emerald-100 hover:bg-emerald-200 text-emerald-700'
                : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
            }`}
          >
            <Brain className="w-4 h-4" />
            {updatingAI === respondent.id 
              ? 'Updating...' 
              : respondent.aiAnalysisEnabled 
                ? 'Enabled' 
                : 'Disabled'
            }
          </button>
        );
      },
      filterFn: (row, columnId, value) => {
        if (value === 'all') return true;
        const enabled = row.getValue(columnId) as boolean;
        return value === 'enabled' ? enabled : !enabled;
      },
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      cell: (info) => {
        const respondent = info.row.original;
        return (
          <div className="flex items-center gap-2">
            {respondent.assessmentStatus === 'completed' && (
              <Link
                to={`/coach/results/${respondent.userId}`}
                className="p-2 rounded-lg bg-blue-100 text-blue-600 hover:bg-blue-200 transition-all"
                title="View Results"
              >
                <Eye className="w-4 h-4" />
              </Link>
            )}
          </div>
        );
      },
    }),
  ], [updatingAI]);

  const filteredData = useMemo(() => {
    return respondents.filter(respondent => {
      // Status filter
      if (statusFilter !== 'all' && respondent.assessmentStatus !== statusFilter) {
        return false;
      }
      
      // AI filter
      if (aiFilter === 'enabled' && !respondent.aiAnalysisEnabled) {
        return false;
      }
      if (aiFilter === 'disabled' && respondent.aiAnalysisEnabled) {
        return false;
      }
      
      return true;
    });
  }, [respondents, statusFilter, aiFilter]);

  const table = useReactTable({
    data: filteredData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    globalFilterFn: 'includesString',
    state: {
      globalFilter,
    },
    onGlobalFilterChange: setGlobalFilter,
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
  });

  useEffect(() => {
    const loadRespondents = async () => {
      if (!auth.currentUser) {
        navigate('/login');
        return;
      }

      try {
        setIsLoading(true);
        const coachId = auth.currentUser.uid;
        
        // Get respondents for this coach
        const respondentsQuery = query(
          collection(db, 'respondents'),
          where('coachId', '==', coachId)
        );
        const respondentsSnapshot = await getDocs(respondentsQuery);
        
        // Get user data and results for each respondent
        const respondentsData = await Promise.all(respondentsSnapshot.docs.map(async (doc) => {
          const data = doc.data();
          
          // Get user data
          const userDoc = await getDocs(query(
            collection(db, 'users'),
            where('userId', '==', data.userId)
          ));
          const userData = userDoc.docs[0]?.data();
          
          const resultsQuery = query(
            collection(db, 'results'),
            where('userId', '==', data.userId)
          );
          const resultsSnapshot = await getDocs(resultsQuery);
          const sortedDocs = [...resultsSnapshot.docs].sort((a, b) =>
            b.data().completedAt.toMillis() - a.data().completedAt.toMillis()
          );
          const totalAssessments = sortedDocs.length;
          const hasResults = totalAssessments > 0;
          const latestResult = hasResults ? sortedDocs[0].data() : null;

          return {
            id: doc.id,
            userId: data.userId,
            fullName: userData?.fullName || '',
            email: userData?.email || '',
            assessmentStatus: hasResults ? 'completed' as const : 'pending' as const,
            completedAt: latestResult?.completedAt?.toDate(),
            totalAssessments,
            aiAnalysisEnabled: data.aiAnalysisEnabled === true
          };
        }));

        setRespondents(respondentsData);
      } catch (err) {
        console.error('Error loading respondents:', err);
        setError('Failed to load respondents');
      } finally {
        setIsLoading(false);
      }
    };

    loadRespondents();
  }, [navigate]);
  
  if (isLoading) {
    return <LoadingSpinner message="Loading respondents..." />;
  }

  return (
    <div className="min-h-screen minimal-bg py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <Link 
          to="/coach" 
          className="inline-flex items-center gap-2 text-gray-600 hover:text-blue-600 mb-8 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Dashboard
        </Link>

        <div className="flex items-center gap-4 mb-8">
          <Users className="w-12 h-12 text-blue-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Respondents</h1>
            <p className="text-gray-600">View and manage your assessment respondents</p>
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 bg-red-500/10 text-red-400 p-4 rounded-xl mb-6">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <p>{error}</p>
          </div>
        )}

        {/* Search and Filters */}
        <div className="bg-white rounded-2xl p-6 mb-8 shadow-sm border border-gray-200">
          <div className="grid md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search respondents..."
                value={globalFilter ?? ''}
                onChange={(e) => setGlobalFilter(e.target.value)}
                className="w-full pl-10 pr-4 py-3 rounded-xl bg-white border border-gray-300 
                  focus:border-blue-500 focus:outline-none text-gray-900"
              />
            </div>
            
            <div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-white border border-gray-300 
                  focus:border-blue-500 focus:outline-none text-gray-900"
              >
                <option value="all">All Status</option>
                <option value="completed">Completed</option>
                <option value="pending">Pending</option>
              </select>
            </div>
            
            <div>
              <select
                value={aiFilter}
                onChange={(e) => setAiFilter(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-white border border-gray-300 
                  focus:border-blue-500 focus:outline-none text-gray-900"
              >
                <option value="all">All AI Status</option>
                <option value="enabled">AI Enabled</option>
                <option value="disabled">AI Disabled</option>
              </select>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <th 
                        key={header.id}
                        className="px-6 py-4 text-left text-sm font-semibold text-gray-900 cursor-pointer hover:bg-gray-100"
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        <div className="flex items-center gap-2">
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                          {{
                            asc: ' ↑',
                            desc: ' ↓',
                          }[header.column.getIsSorted() as string] ?? null}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="divide-y divide-gray-200">
                {table.getRowModel().rows.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="px-6 py-12 text-center text-gray-500">
                      {respondents.length === 0 ? "No respondents found" : "No respondents match your search criteria"}
                    </td>
                  </tr>
                ) : (
                  table.getRowModel().rows.map(row => (
                    <motion.tr
                      key={row.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      {row.getVisibleCells().map(cell => (
                        <td key={cell.id} className="px-6 py-4 whitespace-nowrap">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </motion.tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span>
                  Showing {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} to{' '}
                  {Math.min(
                    (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                    table.getFilteredRowModel().rows.length
                  )}{' '}
                  of {table.getFilteredRowModel().rows.length} results
                </span>
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => table.setPageIndex(0)}
                  disabled={!table.getCanPreviousPage()}
                  className="p-2 rounded-lg bg-white border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronsLeft className="w-4 h-4" />
                </button>
                
                <button
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                  className="p-2 rounded-lg bg-white border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Page</span>
                  <input
                    type="number"
                    value={table.getState().pagination.pageIndex + 1}
                    onChange={(e) => {
                      const page = e.target.value ? Number(e.target.value) - 1 : 0;
                      table.setPageIndex(page);
                    }}
                    className="w-16 px-2 py-1 text-center border border-gray-300 rounded text-sm"
                    min="1"
                    max={table.getPageCount()}
                  />
                  <span className="text-sm text-gray-600">of {table.getPageCount()}</span>
                </div>
                
                <button
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                  className="p-2 rounded-lg bg-white border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
                
                <button
                  onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                  disabled={!table.getCanNextPage()}
                  className="p-2 rounded-lg bg-white border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronsRight className="w-4 h-4" />
                </button>
                
                <select
                  value={table.getState().pagination.pageSize}
                  onChange={(e) => table.setPageSize(Number(e.target.value))}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                >
                  {[5, 10, 20, 50].map(pageSize => (
                    <option key={pageSize} value={pageSize}>
                      Show {pageSize}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
        
        <Toast
          isVisible={showToast}
          message={toastMessage}
          type={toastType}
          onClose={() => setShowToast(false)}
        />
      </div>
    </div>
  );
}